import { paths } from '@/types/api';
import { segmentClient } from '@/util/analytics';
import { fetchClient } from '@/util/fetchClient';

export type SendInquiryFeedbackV2Request =
  paths['/v2/inquiries/{conversationId}/feedback']['post']['requestBody']['content']['application/json'];

/**
 * Sends inquiry feedback and emits the matching Segment event.
 *
 * Analytics identity: `conversation_id` is the guaranteed join key and is always
 * present. `inquiry_id` is best-effort enrichment — the caller sources it from a
 * separate inquiry query that may not have resolved (notably on a dismiss fired
 * during back-navigation), so it is omitted rather than emitted as null when
 * absent. Don't gate the emit/POST on the inquiry being loaded just to attach it;
 * that would risk dropping the dismiss write.
 */
export async function sendInquiryFeedbackV2({
  conversationId,
  inquiryId,
  events,
  feedbackType,
  ...payload
}: SendInquiryFeedbackV2Request & {
  conversationId: string;
  inquiryId?: number;
  events: {
    dismissed: string;
    failed: string;
    submitted: string;
  };
  feedbackType: string;
}) {
  const context = {
    conversation_id: conversationId,
    ...(inquiryId != null && { inquiry_id: inquiryId }),
  };

  try {
    // To-do: Add feedbackType to API request. Needs to be modified on the back-end first.
    const { data } = await fetchClient.POST('/v2/inquiries/{conversationId}/feedback', {
      body: payload,
      params: {
        path: {
          conversationId,
        },
      },
    });

    if (payload.feedbackStatus === 'Submitted') {
      segmentClient.track(events.submitted, context);

      return data;
    }

    if (payload.feedbackStatus === 'Dismissed') {
      segmentClient.track(events.dismissed, context);
    }

    return data;
  } catch (error) {
    segmentClient.track(events.failed, {
      ...context,
      error: error ? JSON.stringify(error) : 'Unknown error',
    });

    throw error;
  }
}
